import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowSquareOut, ClockCounterClockwise, MapPin, Sparkle } from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { StoryCard } from "@/components/StoryCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { getAuthorSlug } from "@/utils/newsroomStaticContent";
import { getStoryPhotoCredit } from "@/utils/storyMedia";
import { getStoryCategoryData } from "@/utils/storyCategorization";
import { getStoryDisplayTitle, getStorySectionLabel } from "@/utils/storySections";
import { getYouTubeEmbedUrl } from "@/utils/videoEmbed";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (value) => {
  try {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const cleanAiCopy = (value) => value
  ?.replace(/\*\*/g, "")
  .replace(/__/g, "")
  .replace(/^#+\s*/gm, "")
  .replace(/\s*\[[^\]]+\]\s*/g, " ")
  .replace(/\s*\((sports|editorial)\)\s*/gi, " ")
  .replace(/[ \t]{2,}/g, " ")
  .trim();

const getSectionPath = (section) => {
  if (section === "breaking") return "/breaking-news";
  if (section === "editorial") return "/whats-new";
  if (section === "sports") return "/sports";
  return `/${section}`;
};

const sanitizeOfficeArticleHtml = (value) => {
  if (!value || typeof window === "undefined") {
    return value;
  }

  const parser = new window.DOMParser();
  const documentFragment = parser.parseFromString(value, "text/html");
  documentFragment.body.querySelectorAll("*").forEach((node) => {
    ["style", "class", "id", "dir", "width", "height"].forEach((attribute) => node.removeAttribute(attribute));
  });

  return documentFragment.body.innerHTML;
};

const getOfficeArticleBlocks = (value) => {
  if (!value || typeof window === "undefined") {
    return value ? [value] : [];
  }

  const parser = new window.DOMParser();
  const documentFragment = parser.parseFromString(value, "text/html");
  return Array.from(documentFragment.body.childNodes)
    .map((node) => node.outerHTML || node.textContent || "")
    .map((content) => content.trim())
    .filter(Boolean);
};

const OfficeArticlePhotoRail = ({ images, title }) => (
  <div className="space-y-4" data-testid="story-detail-inline-gallery">
    {images.map((imageUrl, index) => (
      <div key={`${imageUrl}-${index}`} className="overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-100" data-testid={`story-detail-inline-gallery-image-${index}`}>
        <div className="aspect-[16/10] w-full">
          <img alt={`${title} photo ${index + 1}`} className="h-full w-full object-cover" src={imageUrl} />
        </div>
      </div>
    ))}
  </div>
);

const InlineStoryVideo = ({ embedUrl, title }) => (
  <div className="overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-950" data-testid="story-detail-inline-video">
    <div className="aspect-[16/10] w-full sm:aspect-video">
      <iframe
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="h-full w-full"
        data-testid="story-detail-inline-video-embed"
        referrerPolicy="strict-origin-when-cross-origin"
        src={embedUrl}
        title={title || "Content Watch inline video"}
      />
    </div>
  </div>
);

const pickRelatedStories = (currentStory, feed) => {
  const currentCategories = getStoryCategoryData(currentStory);
  return feed
    .filter((candidate) => candidate.slug !== currentStory.slug)
    .map((candidate) => {
      const candidateCategories = getStoryCategoryData(candidate);
      let score = 0;
      if (candidate.section === currentStory.section) {
        score += 3;
      }
      if (candidateCategories.sceneCategory === currentCategories.sceneCategory) {
        score += 3;
      }
      if (candidateCategories.coverageArea === currentCategories.coverageArea) {
        score += 2;
      }
      if (candidate.platform === currentStory.platform) {
        score += 1;
      }
      return { candidate, score };
    })
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 3)
    .map((item) => item.candidate);
};

export const StoryDetailPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [relatedStories, setRelatedStories] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [deletingStory, setDeletingStory] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = window.localStorage.getItem("content_watch_token");
  const seoTitle = story ? `${getStoryDisplayTitle(story)} | Content Watch HQ` : "Story Update | Content Watch HQ";
  const seoDescription = story?.excerpt || story?.caption || "Follow the latest Content Watch HQ story updates, videos, and local Houston reporting.";
  const authorSlug = story?.author ? getAuthorSlug(story.author) : null;
  const sectionPath = story?.section ? getSectionPath(story.section) : "/breaking-news";

  usePageSeo({
    canonicalPath: `/stories/${slug}`,
    description: seoDescription,
    image: story?.thumbnail_url || story?.image_url,
    schema: story ? [
      {
        "@context": "https://schema.org",
        "@type": ["NewsArticle", "Article"],
        headline: getStoryDisplayTitle(story),
        description: seoDescription,
        datePublished: story.published_at,
        dateModified: story.updated_at || story.published_at,
        image: [story.thumbnail_url || story.image_url].filter(Boolean),
        mainEntityOfPage: `${process.env.REACT_APP_SITE_URL}/stories/${slug}`,
        articleSection: getStorySectionLabel(story.section),
        keywords: (story.tags || []).join(", "),
        author: story.author ? { "@type": "Person", name: story.author, url: `${process.env.REACT_APP_SITE_URL}/authors/${authorSlug}` } : undefined,
        publisher: {
          "@type": "Organization",
          name: "Content Watch HQ",
          logo: { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_content-watch-7/artifacts/thy81wjj_747B42A6-4C25-4A17-A557-B9BBE91C99DB%20Background%20Removed.png" },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: process.env.REACT_APP_SITE_URL },
          { "@type": "ListItem", position: 2, name: getStorySectionLabel(story.section), item: `${process.env.REACT_APP_SITE_URL}${sectionPath}` },
          { "@type": "ListItem", position: 3, name: getStoryDisplayTitle(story), item: `${process.env.REACT_APP_SITE_URL}/stories/${slug}` },
        ],
      },
    ] : [],
    title: seoTitle,
    type: "article",
  });

  useEffect(() => {
    let mounted = true;
    const loadStory = async () => {
      try {
        const authRequest = token
          ? axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {
            window.localStorage.removeItem("content_watch_token");
            return null;
          })
          : Promise.resolve(null);
        const [storyResponse, feedResponse, authResponse] = await Promise.all([
          axios.get(`${API_BASE}/newsfeed/${slug}`),
          axios.get(`${API_BASE}/newsfeed`),
          authRequest,
        ]);
        if (mounted) {
          setStory(storyResponse.data);
          setRelatedStories(pickRelatedStories(storyResponse.data, feedResponse.data));
          setCurrentUser(authResponse?.data || null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadStory();
    return () => {
      mounted = false;
    };
  }, [slug, token]);

  const handleDeleteStory = async () => {
    const confirmed = window.confirm("This will remove the story from the public site and place it into Colton's archive vault. Continue?");
    if (!confirmed) {
      return;
    }

    setDeletingStory(true);
    try {
      await axios.delete(`${API_BASE}/office/stories/${story.slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Story removed from the website and archived.");
      navigate("/office-use");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete did not complete.");
    } finally {
      setDeletingStory(false);
    }
  };

  if (loading) {
    return (
      <main className="page-shell mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="story-detail-loading">
        <div className="border border-[rgba(10,15,26,0.12)] bg-white p-8">
          <p className="eyebrow text-red-600">Loading story</p>
          <h1 className="mt-3 font-['Outfit'] text-4xl font-black tracking-tight text-slate-950">Preparing the live video story page.</h1>
        </div>
      </main>
    );
  }

  if (!story) {
    return (
      <main className="page-shell mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="story-detail-missing">
        <div className="border border-[rgba(10,15,26,0.12)] bg-white p-8">
          <p className="eyebrow text-red-600">Story unavailable</p>
          <h1 className="mt-3 font-['Outfit'] text-4xl font-black tracking-tight text-slate-950">We couldn’t find that story.</h1>
        </div>
      </main>
    );
  }

  const displayTitle = getStoryDisplayTitle(story);
  const { sceneCategory, coverageArea } = getStoryCategoryData(story);
  const sectionLabel = getStorySectionLabel(story.section);
  const galleryImages = story.image_gallery?.length ? story.image_gallery : story.image_url ? [story.image_url] : [];
  const isOfficeUploadedArticle = story.platform === "manual" && Boolean(story.body_html);
  const leadArticleImage = isOfficeUploadedArticle ? galleryImages[0] : null;
  const trailingArticleImages = isOfficeUploadedArticle ? galleryImages.slice(1) : galleryImages;
  const hasVideoSource = Boolean(story.video_url || story.permalink);
  const showInlineHeroVideo = isOfficeUploadedArticle && hasVideoSource;
  const showSidebarMedia = !isOfficeUploadedArticle;
  const shouldRenderSidebar = showSidebarMedia || Boolean(currentUser?.can_delete) || Boolean(story.permalink);
  const normalizedOfficeBodyHtml = isOfficeUploadedArticle ? sanitizeOfficeArticleHtml(story.body_html) : story.body_html;
  const supportingImages = story.supporting_image_gallery || [];
  const supportingMediaItems = story.supporting_media_items || [];
  const bottomGalleryImages = story.summary_image_gallery?.length ? story.summary_image_gallery : trailingArticleImages;
  const photoCredit = getStoryPhotoCredit(story);
  const inlineEmbedUrl = getYouTubeEmbedUrl(story.video_url || story.permalink);
  const officeArticleBlocks = isOfficeUploadedArticle ? getOfficeArticleBlocks(normalizedOfficeBodyHtml) : [];
  const supportingImagesByParagraph = supportingMediaItems.reduce((groups, item) => {
    const insertAfterParagraph = Number(item.insert_after_paragraph || item.insertAfterParagraph || 1);
    const key = Number.isNaN(insertAfterParagraph) ? 1 : Math.max(insertAfterParagraph, 1);
    if (!groups[key]) {
      groups[key] = [];
    }
    if (item.url) {
      groups[key].push(item.url);
    }
    return groups;
  }, {});

  const renderPhotoCredit = (testId) => (photoCredit ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500" data-testid={testId}>{photoCredit}</p> : null);

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="story-detail-page">
      <div className="mb-6">
        <Button asChild className="rounded-none border border-slate-950 bg-transparent text-xs font-bold uppercase tracking-[0.2em] text-slate-950 hover:bg-slate-950 hover:text-white" data-testid="story-detail-back-button" variant="ghost">
          <Link to={sectionPath}>
            <ArrowLeft size={16} weight="bold" />
            Back to {sectionLabel}
          </Link>
        </Button>
      </div>

      <section className={`grid gap-8 ${shouldRenderSidebar ? (showSidebarMedia ? "xl:grid-cols-[minmax(0,1fr)_360px]" : "xl:grid-cols-[minmax(0,1fr)_300px]") : "xl:grid-cols-1"}`} data-testid="story-detail-layout">
        <div className={`space-y-6 ${isOfficeUploadedArticle ? "w-full xl:max-w-4xl" : ""}`}>
          <div className="border border-[rgba(10,15,26,0.12)] bg-white p-6 sm:p-8" data-testid="story-detail-copy-panel">
            <p className="eyebrow text-red-600" data-testid="story-detail-overline">{sectionLabel}</p>
            <h1 className="mt-3 font-['Outfit'] text-4xl font-black tracking-tight text-slate-950 sm:text-5xl" data-testid="story-detail-title">{displayTitle}</h1>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-600">
              {story.author ? (
                <Link className="inline-flex items-center gap-2 transition-colors hover:text-[#1338BE]" data-testid="story-detail-author" to={`/authors/${authorSlug}`}>
                  By {story.author}
                </Link>
              ) : null}
              <span className="inline-flex items-center gap-2" data-testid="story-detail-published-time">
                <ClockCounterClockwise size={16} weight="bold" />
                Published on: {formatTime(story.published_at)}
              </span>
              <span className="inline-flex items-center gap-2" data-testid="story-detail-updated-time">
                <ClockCounterClockwise size={16} weight="bold" />
                Updated on: {formatTime(story.updated_at || story.published_at)}
              </span>
              <span className="inline-flex items-center gap-2" data-testid="story-detail-location">
                <MapPin size={16} weight="bold" />
                {story.location}
              </span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="story-detail-category-group">
              <span className="border border-[rgba(10,15,26,0.12)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700" data-testid="story-detail-section-label">{sectionLabel}</span>
              <span className="border border-[rgba(210,20,4,0.18)] bg-[rgba(210,20,4,0.06)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#D21404]" data-testid="story-detail-scene-category">{sceneCategory}</span>
              <span className="border border-[rgba(10,15,26,0.12)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700" data-testid="story-detail-coverage-area">{coverageArea}</span>
            </div>
            {leadArticleImage ? (
              <div className="mt-6" data-testid="story-detail-lead-image">
                <div className="overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-100">
                  <div className="aspect-[4/3] w-full sm:aspect-[16/10]">
                    <img alt={`${displayTitle} main photo`} className="h-full w-full object-cover" src={leadArticleImage} />
                  </div>
                </div>
                {renderPhotoCredit("story-detail-lead-image-credit")}
              </div>
            ) : null}
            {showInlineHeroVideo && inlineEmbedUrl ? (
              <div className="mt-6">
                <InlineStoryVideo embedUrl={inlineEmbedUrl} title={displayTitle} />
              </div>
            ) : null}
            {!isOfficeUploadedArticle ? <p className="mt-6 whitespace-pre-line text-base leading-8 text-slate-700" data-testid="story-detail-excerpt">{story.excerpt}</p> : null}

            {story.body_html && isOfficeUploadedArticle ? (
              <div className="mt-6 space-y-8" data-testid="story-detail-office-body-flow">
                {officeArticleBlocks.map((blockHtml, index) => (
                  <div key={`office-article-block-${index}`} className="space-y-6">
                    <div className="story-article-body prose prose-slate max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: blockHtml }} data-testid={index === 0 ? "story-detail-body-html" : `story-detail-body-html-${index}`} />
                    {supportingImagesByParagraph[index + 1]?.length ? (
                      <div className="pt-2" data-testid={`story-detail-supporting-insert-${index + 1}`}>
                        <p className="eyebrow text-red-600">Supporting photos after paragraph {index + 1}</p>
                        <div className="mt-5">
                          <OfficeArticlePhotoRail images={supportingImagesByParagraph[index + 1]} title={displayTitle} />
                        </div>
                        {renderPhotoCredit(`story-detail-supporting-gallery-credit-${index + 1}`)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : story.body_html ? (
              <div className="story-article-body prose prose-slate mt-6 max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: normalizedOfficeBodyHtml }} data-testid="story-detail-body-html" />
            ) : (
              <p className="mt-6 whitespace-pre-line text-base leading-8 text-slate-700" data-testid="story-detail-excerpt">{story.excerpt}</p>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              {story.tags?.map((tag) => (
                <span key={`${story.slug}-${tag}`} className="border border-[rgba(10,15,26,0.12)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700" data-testid={`story-detail-tag-${tag}`}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {story.section === "breaking" ? (
            <div className="border border-[rgba(0,47,167,0.18)] bg-[rgba(0,47,167,0.04)] p-6 sm:p-8" data-testid="story-detail-ai-panel">
              <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#1338BE]">
                <Sparkle size={16} weight="fill" />
                General Overview of Situation
              </div>
              <p className="whitespace-pre-line text-base leading-8 text-slate-700" data-testid="story-detail-ai-copy">{cleanAiCopy(story.ai_story) || story.caption}</p>
            </div>
          ) : null}

          {!isOfficeUploadedArticle ? (
            <div className="border border-[rgba(10,15,26,0.12)] bg-white p-6 sm:p-8" data-testid="story-detail-caption-panel">
              <p className="eyebrow text-slate-500">Original upload context</p>
              <p className="mt-4 whitespace-pre-line text-base leading-8 text-slate-700" data-testid="story-detail-caption">{story.caption}</p>
            </div>
          ) : null}

          {!isOfficeUploadedArticle && galleryImages.length ? (
            <div className="border border-[rgba(10,15,26,0.12)] bg-white p-6 sm:p-8" data-testid="story-detail-gallery-panel">
              <p className="eyebrow text-red-600">Photo gallery</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {galleryImages.map((imageUrl, index) => (
                  <div key={`${imageUrl}-${index}`} className="overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-100 aspect-[4/3]" data-testid={`story-detail-gallery-image-${index}`}>
                    <img alt={`${displayTitle} ${index + 1}`} className="h-full w-full object-cover" src={imageUrl} />
                  </div>
                ))}
              </div>
              {renderPhotoCredit("story-detail-gallery-credit")}
            </div>
          ) : null}

          {isOfficeUploadedArticle && bottomGalleryImages.length ? (
            <div className="border border-[rgba(10,15,26,0.12)] bg-white p-6 sm:p-8" data-testid="story-detail-bottom-gallery-panel">
              <p className="eyebrow text-red-600">Story summary gallery</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {bottomGalleryImages.map((imageUrl, index) => (
                  <div key={`${imageUrl}-${index}`} className="overflow-hidden border border-[rgba(10,15,26,0.12)] bg-slate-100 aspect-[4/3]" data-testid={`story-detail-bottom-gallery-image-${index}`}>
                    <img alt={`${displayTitle} summary ${index + 1}`} className="h-full w-full object-cover" src={imageUrl} />
                  </div>
                ))}
              </div>
              {renderPhotoCredit("story-detail-bottom-gallery-credit")}
            </div>
          ) : null}
        </div>

        {shouldRenderSidebar ? (
          <aside className="space-y-5" data-testid="story-detail-video-sidebar">
            {showSidebarMedia ? <VerticalVideoFrame story={story} /> : null}
            {currentUser?.can_delete ? (
              <Button className="w-full rounded-none bg-[#D21404] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="story-detail-delete-button" disabled={deletingStory} onClick={handleDeleteStory} type="button">
                {deletingStory ? "Archiving" : "Delete + Archive Story"}
              </Button>
            ) : null}
            {story.permalink ? (
              <Button asChild className="w-full rounded-none bg-[#D21404] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="story-detail-youtube-button">
                <a href={story.permalink} rel="noreferrer" target="_blank">
                  Open original source
                  <ArrowSquareOut size={16} weight="bold" />
                </a>
              </Button>
            ) : null}
          </aside>
        ) : null}
      </section>

      {relatedStories.length ? (
        <section className="mt-8" data-testid="story-detail-related-section">
          <div className="mb-6 max-w-3xl">
            <p className="eyebrow text-red-600" data-testid="story-detail-related-overline">Related coverage</p>
            <h2 className="headline-lg mt-3" data-testid="story-detail-related-title">Keep watching the same scene with the next most relevant stories.</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="story-detail-related-grid">
            {relatedStories.map((relatedStory) => (
              <StoryCard key={relatedStory.id} story={relatedStory} />
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
};